import React, { useState, useEffect } from "react";
import {
  Users,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  GitCompare,
  Clock,
  ArrowUpDown,
  Star,
  Bookmark,
  Target,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  addDoc,
} from "firebase/firestore";
import toast from "react-hot-toast";

// Import new components
import CandidateFilters from "../../components/HR/CandidateFilters";
import ResumePreviewModal from "../../components/HR/ResumePreviewModal";
import CandidateComparison from "../../components/HR/CandidateComparison";
import VerifiedBadge from "../../components/VerifiedBadge";

// Import utilities
import {
  calculateSkillsMatch,
  extractSkillsFromJD,
  formatTimeline,
  formatDaysAgo,
  exportToCSV,
  filterCandidates,
  sortCandidates,
  type SortCriteria,
} from "../../utils/candidateUtils";

const CandidateManagement: React.FC = () => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  // State
  const [candidates, setCandidates] = useState<any[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<any[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedCandidates, setSavedCandidates] = useState<string[]>([]);

  // Modals
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeModalData, setResumeModalData] = useState<any>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState<any>({});
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [sortBy, setSortBy] = useState<SortCriteria>("totalScore");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // UI State - context panel removed for this page as per user request

  // Fetch saved candidates
  useEffect(() => {
    if (!user) return;

    const fetchSavedCandidates = async () => {
      try {
        const savedDoc = await getDoc(doc(db, "hrSavedCandidates", user.id));
        if (savedDoc.exists()) {
          const data = savedDoc.data();
          setSavedCandidates(data.candidateIds || []);
        }
      } catch (error) {
      }
    };

    fetchSavedCandidates();
  }, [user, db]);

  // Fetch candidates data
  useEffect(() => {
    if (!user) return;

    const setupListeners = async () => {
      try {
        // Get all tests created by this HR
        const hrTestsQuery = query(
          collection(db, "hrTests"),
          where("hrId", "==", user.id)
        );

        const unsubscribeTests = onSnapshot(hrTestsQuery, (hrTestsSnapshot) => {
          const hrTestIds = hrTestsSnapshot.docs.map((doc) => doc.id);

          if (hrTestIds.length === 0) {
            // Try direct query as fallback
            const directResultsQuery = query(
              collection(db, "jobseekerResults"),
              where("hrId", "==", user.id),
              orderBy("completedAt", "desc")
            );

            const unsubscribeDirectResults = onSnapshot(
              directResultsQuery,
              async (directSnapshot) => {
                await processResults(directSnapshot.docs);
              }
            );

            return () => unsubscribeDirectResults();
          }

          // Query results for HR's tests
          const resultsQuery = query(
            collection(db, "jobseekerResults"),
            where("testId", "in", hrTestIds),
            orderBy("completedAt", "desc")
          );

          const unsubscribeResults = onSnapshot(resultsQuery, async (resultsSnapshot) => {
            await processResults(resultsSnapshot.docs);
          });

          return () => unsubscribeResults();
        });

        return () => unsubscribeTests();
      } catch (error) {
        setLoading(false);
      }
    };

    const processResults = async (resultsDocs: any[]) => {
      try {
        const results = resultsDocs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch user profiles and shortlist/reject status for enrichment
        const enrichedCandidates = await Promise.all(
          results.map(async (result: any) => {
            let profileData: any = {};
            let shortlistedData: any = null;
            let rejectedData: any = null;

            // Fetch profile data
            try {
              const profileDoc = await getDoc(
                doc(db, "userProfiles", result.jobSeekerId)
              );
              if (profileDoc.exists()) {
                profileData = profileDoc.data();
              }
            } catch (err) {
            }

            // Check if shortlisted
            try {
              const shortlistedQuery = query(
                collection(db, "hrShortlisted"),
                where("hrId", "==", user.id),
                where("candidateId", "==", result.jobSeekerId)
              );
              const shortlistedSnapshot = await getDocs(shortlistedQuery);
              if (!shortlistedSnapshot.empty) {
                shortlistedData = {
                  id: shortlistedSnapshot.docs[0].id,
                  ...shortlistedSnapshot.docs[0].data(),
                };
              }
            } catch (err) {
              // Ignore
            }

            // Check if rejected
            try {
              const rejectedQuery = query(
                collection(db, "hrRejected"),
                where("hrId", "==", user.id),
                where("candidateId", "==", result.jobSeekerId)
              );
              const rejectedSnapshot = await getDocs(rejectedQuery);
              if (!rejectedSnapshot.empty) {
                rejectedData = {
                  id: rejectedSnapshot.docs[0].id,
                  ...rejectedSnapshot.docs[0].data(),
                };
              }
            } catch (err) {
              // Ignore
            }

            // Calculate skills match if job is selected
            let skillsMatchPercentage = undefined;
            if (filters.jobId && selectedJob) {
              const requiredSkills = extractSkillsFromJD(selectedJob.jobDescription || "");
              const candidateSkills =
                profileData.resumeData?.professionalInfo?.skills || result.skills || [];
              skillsMatchPercentage = calculateSkillsMatch(
                candidateSkills,
                requiredSkills
              );
            }

            // Format timeline
            const timeline = formatTimeline({
              completedAt: result.completedAt,
              shortlistedAt: shortlistedData?.shortlistedAt,
              rejectedAt: rejectedData?.rejectedAt,
            });

            return {
              id: result.jobSeekerId || result.id,
              resultId: result.id,
              name: result.jobSeekerName || "Unknown Candidate",
              email: result.jobSeekerEmail || "No email provided",
              position: result.jobTitle || "Unknown Position",
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(
                result.jobSeekerName || "Unknown"
              )}&background=ef4444&color=fff`,
              status: result.hasCompletedInterview
                ? result.recommendationForHiring === "STRONG RECOMMEND" ||
                  result.recommendationForHiring === "RECOMMEND"
                  ? "interview_completed_recommended"
                  : "interview_completed"
                : result.qualifiedForInterview === false
                ? "test_failed"
                : "test_completed",
              mcqScore: result.mcqScore || 0,
              interviewScore: result.interviewScore || 0,
              totalScore: result.totalScore || result.mcqScore || 0,
              hasCompletedInterview: result.hasCompletedInterview || false,
              qualifiedForInterview: result.qualifiedForInterview !== false,
              recommendationForHiring: result.recommendationForHiring || "NEUTRAL",
              keyFindings: result.keyFindings || "",
              skills:
                profileData.resumeData?.professionalInfo?.skills || result.skills || [],
              experience:
                profileData.resumeData?.professionalInfo?.experience || "Not specified",
              location: result.location || "Not specified",
              appliedDate:
                result.completedAt?.toDate?.()?.toLocaleDateString() ||
                new Date().toLocaleDateString(),
              completedAt: result.completedAt,
              skillsVerified: profileData.skillsVerified || false,
              skillsMatchPercentage,
              timeline,
              isShortlisted: !!shortlistedData,
              isRejected: !!rejectedData,
              shortlistedData,
              rejectedData,
              testId: result.testId,
              testTitle: result.testTitle,
            };
          })
        );

        // Remove duplicates (keep latest)
        const uniqueCandidates = enrichedCandidates.reduce(
          (acc: any[], current: any) => {
            const existing = acc.find((item) => item.id === current.id);
            if (
              !existing ||
              new Date(current.appliedDate) > new Date(existing.appliedDate)
            ) {
              return [...acc.filter((item) => item.id !== current.id), current];
            }
            return acc;
          },
          []
        );

        setCandidates(uniqueCandidates);
        setLoading(false);
      } catch (error) {
        setCandidates([]);
        setLoading(false);
      }
    };

    setupListeners();
  }, [user, db, filters.jobId, selectedJob]);

  // Apply filters and sorting
  useEffect(() => {
    let result = filterCandidates(candidates, filters);
    result = sortCandidates(result, sortBy, sortDirection);
    setFilteredCandidates(result);
  }, [candidates, filters, sortBy, sortDirection]);

  // Fetch selected job when filter changes
  useEffect(() => {
    if (!filters.jobId) {
      setSelectedJob(null);
      return;
    }

    const fetchJob = async () => {
      try {
        const jobDoc = await getDoc(doc(db, "hrJobs", filters.jobId));
        if (jobDoc.exists()) {
          setSelectedJob({ id: jobDoc.id, ...jobDoc.data() });
        }
      } catch (error) {
      }
    };

    fetchJob();
  }, [filters.jobId, db]);

  // Handlers
  const handleFilterChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const handleSelectCandidate = (candidateId: string) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map((c) => c.id));
    }
  };

  const handleSaveCandidate = async (candidateId: string) => {
    if (!user) return;

    try {
      const isCurrentlySaved = savedCandidates.includes(candidateId);
      const newSavedCandidates = isCurrentlySaved
        ? savedCandidates.filter((id) => id !== candidateId)
        : [...savedCandidates, candidateId];

      await setDoc(doc(db, "hrSavedCandidates", user.id), {
        candidateIds: newSavedCandidates,
        hrId: user.id,
        lastUpdated: new Date(),
      });

      setSavedCandidates(newSavedCandidates);
      toast.success(
        isCurrentlySaved ? "Removed from saved" : "Candidate saved!"
      );
    } catch (error) {
      toast.error("Failed to save candidate");
    }
  };

  const handleViewResume = (candidate: any) => {
    setResumeModalData({ candidateId: candidate.id, candidateName: candidate.name });
    setShowResumeModal(true);
  };

  const handleCompare = () => {
    if (selectedCandidates.length < 2 || selectedCandidates.length > 3) {
      toast.error("Please select 2-3 candidates to compare");
      return;
    }

    setShowComparisonModal(true);
  };

  const handleBulkShortlist = async () => {
    if (selectedCandidates.length === 0) {
      toast.error("Please select candidates first");
      return;
    }

    try {
      const promises = selectedCandidates.map(async (candidateId) => {
        const candidate = filteredCandidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        await addDoc(collection(db, "hrShortlisted"), {
          hrId: user!.id,
          candidateId: candidate.id,
          candidateName: candidate.name || "Unknown",
          candidateEmail: candidate.email || "",
          testId: candidate.testId || "",
          testTitle: candidate.testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          shortlistedAt: new Date(),
        });
      });

      await Promise.all(promises);
      setSelectedCandidates([]);
      toast.success(`Shortlisted ${selectedCandidates.length} candidates!`);
    } catch (error) {
      toast.error("Failed to shortlist candidates");
    }
  };

  const handleBulkReject = async () => {
    if (selectedCandidates.length === 0) {
      toast.error("Please select candidates first");
      return;
    }

    try {
      const promises = selectedCandidates.map(async (candidateId) => {
        const candidate = filteredCandidates.find((c) => c.id === candidateId);
        if (!candidate) return;

        await addDoc(collection(db, "hrRejected"), {
          hrId: user!.id,
          candidateId: candidate.id,
          candidateName: candidate.name || "Unknown",
          candidateEmail: candidate.email || "",
          testId: candidate.testId || "",
          testTitle: candidate.testTitle || "Unknown Test",
          totalScore: candidate.totalScore || 0,
          recommendationForHiring: candidate.recommendationForHiring || "NEUTRAL",
          rejectedAt: new Date(),
        });
      });

      await Promise.all(promises);
      setSelectedCandidates([]);
      toast.success(`Rejected ${selectedCandidates.length} candidates`);
    } catch (error) {
      toast.error("Failed to reject candidates");
    }
  };

  const handleExportCSV = () => {
    exportToCSV(filteredCandidates, `candidates_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Candidates exported to CSV!");
  };

  const toggleSort = (criteria: SortCriteria) => {
    if (sortBy === criteria) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(criteria);
      setSortDirection("desc");
    }
  };

  // Get top 5 AI recommended candidates
  const topRecommended = filteredCandidates
    .filter(
      (c) =>
        c.recommendationForHiring === "STRONG RECOMMEND" ||
        c.recommendationForHiring === "RECOMMEND"
    )
    .slice(0, 5);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-red-600" />
            Candidate Management
          </h1>
          <p className="text-gray-600 mt-1">
            Advanced candidate screening and management
          </p>
        </div>
      </div>

      {/* Main Layout - 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Column - Filters */}
        <div className="lg:col-span-1 space-y-6">
          <CandidateFilters
            onFilterChange={handleFilterChange}
            candidatesCount={filteredCandidates.length}
          />
        </div>

        {/* Right Column - Candidates List */}
        <div className="lg:col-span-3 space-y-6">
          {/* Top 5 AI Recommended */}
          {topRecommended.length > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border-2 border-green-300 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 text-yellow-500 mr-2" />
                Top 5 AI Recommended Candidates
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 overflow-hidden">
                {topRecommended.map((candidate) => (
                  <div
                    key={candidate.id}
                    className="bg-white rounded-lg p-3 border border-green-200 hover:border-green-400 transition-all cursor-pointer overflow-hidden"
                    onClick={() => handleViewResume(candidate)}
                  >
                    <div className="flex items-center space-x-2 mb-2 overflow-hidden">
                      <img
                        src={candidate.avatar}
                        alt={candidate.name}
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="font-semibold text-gray-900 text-sm truncate">
                          {candidate.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {candidate.position}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-lg font-bold text-green-600 flex-shrink-0">
                        {candidate.totalScore?.toFixed(0)}%
                      </span>
                      {candidate.skillsMatchPercentage !== undefined && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full whitespace-nowrap">
                          <Target className="w-3 h-3 inline mr-1" />
                          {candidate.skillsMatchPercentage}% match
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  checked={
                    selectedCandidates.length === filteredCandidates.length &&
                    filteredCandidates.length > 0
                  }
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">
                  {selectedCandidates.length > 0
                    ? `${selectedCandidates.length} selected`
                    : `${filteredCandidates.length} candidates`}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {selectedCandidates.length > 0 && (
                  <>
                    <button
                      onClick={handleBulkShortlist}
                      className="px-3 py-1.5 bg-green-100 text-green-800 text-sm rounded-lg hover:bg-green-200 transition-colors flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Shortlist
                    </button>
                    <button
                      onClick={handleBulkReject}
                      className="px-3 py-1.5 bg-red-100 text-red-800 text-sm rounded-lg hover:bg-red-200 transition-colors flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                    {selectedCandidates.length >= 2 && selectedCandidates.length <= 3 && (
                      <button
                        onClick={handleCompare}
                        className="px-3 py-1.5 bg-blue-100 text-blue-800 text-sm rounded-lg hover:bg-blue-200 transition-colors flex items-center"
                      >
                        <GitCompare className="w-4 h-4 mr-1" />
                        Compare
                      </button>
                    )}
                  </>
                )}
                <button
                  onClick={handleExportCSV}
                  className="px-3 py-1.5 bg-gray-100 text-gray-800 text-sm rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200">
              <span className="text-xs sm:text-sm text-gray-600 mr-1 sm:mr-2 whitespace-nowrap">Sort by:</span>
              <button
                onClick={() => toggleSort("totalScore")}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm flex items-center transition-colors whitespace-nowrap ${
                  sortBy === "totalScore"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="hidden sm:inline">Total Score</span>
                <span className="sm:hidden">Score</span>
                {sortBy === "totalScore" && (
                  <ArrowUpDown className="w-3 h-3 ml-1 flex-shrink-0" />
                )}
              </button>
              {filters.jobId && (
                <button
                  onClick={() => toggleSort("skillsMatch")}
                  className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm flex items-center transition-colors whitespace-nowrap ${
                    sortBy === "skillsMatch"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <span className="hidden sm:inline">Skills Match</span>
                  <span className="sm:hidden">Skills</span>
                  {sortBy === "skillsMatch" && (
                    <ArrowUpDown className="w-3 h-3 ml-1 flex-shrink-0" />
                  )}
                </button>
              )}
              <button
                onClick={() => toggleSort("appliedDate")}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm flex items-center transition-colors whitespace-nowrap ${
                  sortBy === "appliedDate"
                    ? "bg-red-100 text-red-800"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span className="hidden sm:inline">Date Applied</span>
                <span className="sm:hidden">Date</span>
                {sortBy === "appliedDate" && (
                  <ArrowUpDown className="w-3 h-3 ml-1 flex-shrink-0" />
                )}
              </button>
            </div>
          </div>

          {/* Candidates Grid */}
          {filteredCandidates.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-gray-500">
                {candidates.length === 0 ? (
                  <div>
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">No candidates yet</p>
                    <p className="text-sm">
                      Candidates will appear here after they complete your tests.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-lg font-medium">No candidates match your filters</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredCandidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className={`bg-white border rounded-xl p-5 hover:shadow-lg transition-all relative ${
                    savedCandidates.includes(candidate.id)
                      ? "border-red-300 bg-red-50"
                      : candidate.isShortlisted
                      ? "border-green-300 bg-green-50"
                      : candidate.isRejected
                      ? "border-gray-300 bg-gray-50"
                      : "border-gray-200 hover:border-red-300"
                  }`}
                >
                  {/* Checkbox */}
                  <div className="absolute top-4 left-4">
                    <input
                      type="checkbox"
                      checked={selectedCandidates.includes(candidate.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectCandidate(candidate.id);
                      }}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveCandidate(candidate.id);
                      }}
                      className={`p-2 rounded-full transition-colors ${
                        savedCandidates.includes(candidate.id)
                          ? "bg-red-100 text-red-600"
                          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                      }`}
                    >
                      <Bookmark
                        className={`w-4 h-4 ${
                          savedCandidates.includes(candidate.id) ? "fill-current" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="mt-8">
                    {/* Avatar & Info */}
                    <div className="flex items-start space-x-3 mb-4 overflow-hidden">
                      <img
                        src={candidate.avatar}
                        alt={candidate.name}
                        className="w-14 h-14 rounded-full flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2 mb-1 overflow-hidden">
                          <h3 className="text-lg font-bold text-gray-900 truncate flex-1 min-w-0">
                            {candidate.name}
                          </h3>
                          <VerifiedBadge isVerified={candidate.skillsVerified} size="sm" />
                        </div>
                        <p className="text-sm text-gray-600 truncate">{candidate.email}</p>
                        <p className="text-xs text-gray-500 truncate">{candidate.position}</p>
                      </div>
                    </div>

                    {/* Scores */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-gray-600">MCQ</span>
                        <span className="font-semibold text-blue-600">
                          {candidate.mcqScore.toFixed(1)}/25
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-gray-600">Interview</span>
                        <span className="font-semibold text-green-600">
                          {candidate.hasCompletedInterview
                            ? `${candidate.interviewScore.toFixed(1)}/75`
                            : "Pending"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200">
                        <span className="font-bold text-gray-900">Total</span>
                        <span className="font-bold text-red-600 text-lg">
                          {candidate.totalScore.toFixed(0)}%
                        </span>
                      </div>
                    </div>

                    {/* Skills Match */}
                    {candidate.skillsMatchPercentage !== undefined && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">Skills Match</span>
                          <span className="text-xs font-bold text-purple-600">
                            {candidate.skillsMatchPercentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all"
                            style={{ width: `${candidate.skillsMatchPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Timeline */}
                    {candidate.timeline && candidate.timeline.length > 0 && (
                      <div className="mb-3 text-xs text-gray-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {candidate.timeline[0].event} {formatDaysAgo(candidate.timeline[0].daysAgo)}
                      </div>
                    )}

                    {/* Skills */}
                    {candidate.skills.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 3).map((skill: string, idx: number) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                          >
                            {skill}
                          </span>
                        ))}
                        {candidate.skills.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                            +{candidate.skills.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewResume(candidate)}
                        className="flex-1 px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium flex items-center justify-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Resume
                      </button>
                      {candidate.isShortlisted ? (
                        <div className="flex-1 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Shortlisted
                        </div>
                      ) : candidate.isRejected ? (
                        <div className="flex-1 px-3 py-2 bg-gray-100 text-gray-800 rounded-lg text-sm font-medium flex items-center justify-center">
                          <XCircle className="w-4 h-4 mr-1" />
                          Rejected
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showResumeModal && resumeModalData && (
        <ResumePreviewModal
          candidateId={resumeModalData.candidateId}
          candidateName={resumeModalData.candidateName}
          isOpen={showResumeModal}
          onClose={() => {
            setShowResumeModal(false);
            setResumeModalData(null);
          }}
        />
      )}

      {showComparisonModal && (
        <CandidateComparison
          candidates={filteredCandidates.filter((c) =>
            selectedCandidates.includes(c.id)
          )}
          isOpen={showComparisonModal}
          onClose={() => setShowComparisonModal(false)}
        />
      )}
    </div>
  );
};

export default CandidateManagement;
